/**
 * DemoScenarioProjections - For demo mode (no API calls)
 *
 * A self-contained component that calculates scenarios locally
 * without requiring a backend API call.
 * Light theme version matching the site design.
 */

import React, { useState, useEffect } from 'react';
import { Sliders, TrendingUp, TrendingDown, BarChart3, HelpCircle, ChevronDown } from 'lucide-react';
import { formatCurrency } from '../utils/financialFormat';

// Info tooltip component
const InfoTooltip = ({ text }: { text: string }) => (
  <div className="group relative inline-block ml-1">
    <HelpCircle className="w-4 h-4 cursor-help inline" style={{ color: 'var(--color-text-dim)' }} />
    <div className="invisible group-hover:visible absolute z-50 w-64 p-2 text-xs rounded-lg shadow-lg -left-28 top-6 border" style={{ color: 'var(--color-text-secondary)', backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
      {text}
      <div className="absolute -top-1 left-1/2 transform -translate-x-1/2 w-2 h-2 border-l border-t rotate-45" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}></div>
    </div>
  </div>
);

interface DemoScenarioProjectionsProps {
  baseResult: {
    final_claim_amount: number;
    indemnity_period_days?: number;
    daily_gross_profit?: number;
  };
}

export default function DemoScenarioProjections({ baseResult }: DemoScenarioProjectionsProps) {
  // Base values from report
  const baseIndemnity = baseResult?.indemnity_period_days || 90;
  const baseClaim = baseResult?.final_claim_amount || 0;
  const baseDailyGP = baseResult?.daily_gross_profit || Math.round(baseClaim / baseIndemnity);

  // Scenario adjustments
  const [indemnityDays, setIndemnityDays] = useState(baseIndemnity);
  const [revenueDropPct, setRevenueDropPct] = useState(0);
  const [expenseReductionPct, setExpenseReductionPct] = useState(0);

  // Results
  const [scenarioResult, setScenarioResult] = useState<any>(null);
  const [expanded, setExpanded] = useState(true);

  // Calculate scenario locally (no API call)
  useEffect(() => {
    // Simple calculation model for demo purposes
    const indemnityFactor = indemnityDays / baseIndemnity;
    const revenueFactor = 1 - (revenueDropPct / 100);
    const expenseFactor = 1 - (expenseReductionPct / 100);

    // Calculate adjusted claim
    const adjustedDailyGP = baseDailyGP * revenueFactor * expenseFactor;
    const projectedClaim = adjustedDailyGP * indemnityDays;
    const claimDelta = projectedClaim - baseClaim;
    const percentChange = baseClaim > 0 ? ((projectedClaim - baseClaim) / baseClaim) * 100 : 0;

    setScenarioResult({
      base_claim: baseClaim,
      projected_claim: Math.round(projectedClaim),
      claim_delta: Math.round(claimDelta),
      percent_change: percentChange,
      parameters: {
        indemnity_days: indemnityDays,
        revenue_drop_pct: revenueDropPct,
        expense_reduction_pct: expenseReductionPct
      }
    });
  }, [indemnityDays, revenueDropPct, expenseReductionPct, baseClaim, baseDailyGP, baseIndemnity]);

  return (
    <div className="rounded-lg border overflow-hidden" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
      {/* Header - Always Visible */}
      <div
        className="p-4 cursor-pointer transition-colors border-b"
        style={{ borderColor: 'var(--color-border-sub)' }}
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 rounded-lg" style={{ backgroundColor: 'var(--color-needs-human-tint)' }}>
              <Sliders className="w-5 h-5" style={{ color: 'var(--color-needs-human-text)' }} />
            </div>
            <div>
              <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>Loss Projection Scenarios</h3>
              <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
                Adjust parameters to see how changes affect your claim
              </p>
            </div>
          </div>
          <ChevronDown className={`w-5 h-5 transform transition-transform ${expanded ? 'rotate-180' : ''}`} style={{ color: 'var(--color-text-dim)' }} />
        </div>
      </div>

      {/* Expanded Content */}
      {expanded && (
        <div className="p-6 space-y-6">
          {/* Parameter Sliders */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Indemnity Period */}
            <div className="space-y-2">
              <label className="flex items-center text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>
                <TrendingUp className="w-4 h-4 mr-2" style={{ color: 'var(--color-needs-human-text)' }} />
                Indemnity Period (Days)
                <InfoTooltip text="The number of days the business was interrupted. Longer periods typically result in higher claims." />
              </label>
              <input
                type="range"
                min="30"
                max="365"
                value={indemnityDays}
                onChange={(e) => setIndemnityDays(parseInt(e.target.value))}
                className="w-full h-2 rounded-lg appearance-none cursor-pointer"
                style={{ backgroundColor: 'var(--color-bg-elevated)', accentColor: 'var(--color-brand)' }}
              />
              <div className="flex justify-between text-xs" style={{ color: 'var(--color-text-muted)' }}>
                <span>30 days</span>
                <span className="font-semibold" style={{ color: 'var(--color-needs-human-text)' }}>{indemnityDays} days</span>
                <span>365 days</span>
              </div>
            </div>

            {/* Revenue Drop */}
            <div className="space-y-2">
              <label className="flex items-center text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>
                <TrendingDown className="w-4 h-4 mr-2" style={{ color: 'var(--color-needs-human-text)' }} />
                Additional Revenue Drop (%)
                <InfoTooltip text="Additional percentage decline in revenue beyond baseline. Use this to model worse-case scenarios." />
              </label>
              <input
                type="range"
                min="0"
                max="50"
                value={revenueDropPct}
                onChange={(e) => setRevenueDropPct(parseInt(e.target.value))}
                className="w-full h-2 rounded-lg appearance-none cursor-pointer"
                style={{ backgroundColor: 'var(--color-bg-elevated)', accentColor: 'var(--color-brand)' }}
              />
              <div className="flex justify-between text-xs" style={{ color: 'var(--color-text-muted)' }}>
                <span>0%</span>
                <span className="font-semibold" style={{ color: 'var(--color-needs-human-text)' }}>{revenueDropPct}%</span>
                <span>50%</span>
              </div>
            </div>

            {/* Expense Reduction */}
            <div className="space-y-2">
              <label className="flex items-center text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>
                <BarChart3 className="w-4 h-4 mr-2" style={{ color: 'var(--color-verified)' }} />
                Expense Reduction (%)
                <InfoTooltip text="Cost savings achieved during the closure. Higher savings reduce the net claim amount." />
              </label>
              <input
                type="range"
                min="0"
                max="30"
                value={expenseReductionPct}
                onChange={(e) => setExpenseReductionPct(parseInt(e.target.value))}
                className="w-full h-2 rounded-lg appearance-none cursor-pointer"
                style={{ backgroundColor: 'var(--color-bg-elevated)', accentColor: 'var(--color-verified)' }}
              />
              <div className="flex justify-between text-xs" style={{ color: 'var(--color-text-muted)' }}>
                <span>0%</span>
                <span className="font-semibold" style={{ color: 'var(--color-verified)' }}>{expenseReductionPct}%</span>
                <span>30%</span>
              </div>
            </div>
          </div>

          {/* Results Display */}
          {scenarioResult && (
            <div className="rounded-xl p-6 border" style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)' }}>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Base Claim */}
                <div className="text-center">
                  <p className="text-sm mb-1" style={{ color: 'var(--color-text-muted)' }}>Base Claim</p>
                  <p className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>{formatCurrency(scenarioResult.base_claim)}</p>
                </div>

                {/* Projected Claim */}
                <div className="text-center">
                  <p className="text-sm mb-1" style={{ color: 'var(--color-text-muted)' }}>Projected Claim</p>
                  <p className="text-2xl font-bold" style={{
                    color: scenarioResult.projected_claim > scenarioResult.base_claim
                      ? 'var(--color-verified)'
                      : scenarioResult.projected_claim < scenarioResult.base_claim
                        ? 'var(--color-text-muted)'
                        : 'var(--color-text-primary)'
                  }}>
                    {formatCurrency(scenarioResult.projected_claim)}
                  </p>
                </div>

                {/* Change */}
                <div className="text-center">
                  <p className="text-sm mb-1" style={{ color: 'var(--color-text-muted)' }}>Change</p>
                  <p className="text-2xl font-bold" style={{
                    color: scenarioResult.claim_delta > 0
                      ? 'var(--color-verified)'
                      : scenarioResult.claim_delta < 0
                        ? 'var(--color-text-muted)'
                        : 'var(--color-text-primary)'
                  }}>
                    {scenarioResult.claim_delta > 0 ? '+' : ''}
                    {formatCurrency(scenarioResult.claim_delta)}
                    <span className="text-sm ml-1">
                      ({scenarioResult.percent_change > 0 ? '+' : ''}{scenarioResult.percent_change.toFixed(1)}%)
                    </span>
                  </p>
                </div>
              </div>

              {/* Scenario Summary */}
              <div className="mt-6 pt-4 border-t" style={{ borderColor: 'var(--color-border)' }}>
                <p className="text-sm text-center" style={{ color: 'var(--color-text-secondary)' }}>
                  {indemnityDays !== baseIndemnity && (
                    <span className="inline-block px-2 py-1 rounded mr-2 text-xs font-medium" style={{ backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}>
                      Indemnity: {baseIndemnity} → {indemnityDays} days
                    </span>
                  )}
                  {revenueDropPct > 0 && (
                    <span className="inline-block px-2 py-1 rounded mr-2 text-xs font-medium" style={{ backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}>
                      Revenue: -{revenueDropPct}%
                    </span>
                  )}
                  {expenseReductionPct > 0 && (
                    <span className="inline-block px-2 py-1 rounded text-xs font-medium" style={{ backgroundColor: 'transparent', color: 'var(--color-verified)' }}>
                      Expenses: -{expenseReductionPct}%
                    </span>
                  )}
                  {indemnityDays === baseIndemnity && revenueDropPct === 0 && expenseReductionPct === 0 && (
                    <span style={{ color: 'var(--color-text-muted)' }}>Adjust the sliders above to model different scenarios</span>
                  )}
                </p>
              </div>
            </div>
          )}

          {/* Demo Note */}
          <div className="text-center text-xs pt-2" style={{ color: 'var(--color-text-dim)' }}>
            <p>Demo mode: Calculations are simplified. Sign in for full scenario modeling with saved presets.</p>
          </div>
        </div>
      )}
    </div>
  );
}
